import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Alert,
	Box,
	Typography,
	Button,
	Tooltip,
	Divider,
	Grid,
	LinearProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CodeIcon from '@mui/icons-material/Code';
import { IAuthorizationWithCrosses } from 'features/transaction-details/types';
import { AuthorizationTicketsList } from '../AuthorizationTicketsList';
import { getAuthorizationStateName } from 'features/transactions/helpers';
import { formatDateToShowUser, formatDateTimeToShowUser } from 'features/common/helpers';
import { copyToClipboard, formatTransactionAmount } from 'features/transaction-details/helpers';
import { computeAuthorizationBalances } from 'features/transaction-details/helpers/authorizationBalances';
import { authorizationHasEticket } from 'features/transaction-details/helpers/authorizationTickets';
import { AuthorizationRefundSaleOrderNote } from '../AuthorizationRefundSaleOrderNote';
import { AuthorizationCardSummary } from './AuthorizationCardSummary';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { RolesEnum } from 'features/common/types';
import { ICrossWithServiceInfo } from 'features/transaction-details/types';
import { TransactionViewContext } from 'features/transaction-details/types/transactionCrossContext';

interface AuthorizationCardProps {
	authorization: IAuthorizationWithCrosses;
	isHighlighted: boolean;
	highlightEticket?: string;
	operators: { value: string; description: string }[];
	onCopySuccess: (message: string) => void;
	onAddETicket?: (authId: number) => void;
	onUnbindCross?: (authorizationId: number, cross: ICrossWithServiceInfo) => void;
	onRefund?: (authId: number) => void;
	transactionViewContext?: TransactionViewContext;
	onGenerateCommand?: (authId: number) => void;
	dealId?: number;
	saleOrderId?: string | null;
	onRefundNoteError?: (message: string) => void;
}

export const AuthorizationCard = ({
	authorization,
	isHighlighted,
	highlightEticket,
	operators,
	onCopySuccess,
	onAddETicket,
	onUnbindCross,
	onRefund,
	onGenerateCommand,
	dealId,
	saleOrderId,
	onRefundNoteError,
	transactionViewContext,
}: AuthorizationCardProps): JSX.Element => {
	const { auth } = useSelector((state: RootState) => state);
	const [expanded, setExpanded] = useState(isHighlighted);
	const addETicketButtonRef = useRef<HTMLButtonElement>(null);
	const crosses = authorization.crossesData;

	const isAdminOrSuperAdminOrOperator =
		auth.user?.role === RolesEnum.SUPERADMIN ||
		auth.user?.role === RolesEnum.ADMIN ||
		auth.user?.role === RolesEnum.OPERATOR;

	const handleCopyAuthCode = async (e: React.MouseEvent): Promise<void> => {
		e.stopPropagation();
		const success = await copyToClipboard(authorization.authorizationCode);
		if (success) {
			onCopySuccess('Código de autorización copiado');
		}
	};

	const handleCopyAuthId = async (e: React.MouseEvent): Promise<void> => {
		e.stopPropagation();
		const success = await copyToClipboard(authorization.id.toString());
		if (success) {
			onCopySuccess('ID de autorización copiado');
		}
	};

	const balances = computeAuthorizationBalances(authorization);
	const {
		pendingToCross,
		activeCrossed,
		totalRefunded,
		originalAuthorized,
		currentBalance,
		maxRefundable,
		isPaymentGateway,
		canRefund,
		canCross,
		hasActiveCrosses,
		isFullyRefunded,
		isPartiallyRefunded,
	} = balances;
	const hasRefunds = totalRefunded > 0;
	const fmt = (amount: number): string => formatTransactionAmount(amount, authorization.coinCode);
	const ticketCount = new Set(crosses.map((c) => c.eticket)).size;
	const crossedPercent = currentBalance > 0 ? Math.min(100, Math.round((activeCrossed / currentBalance) * 100)) : 0;
	const carrierCode = authorization.chargedAgainstOptionCode;

	const shouldFocusHighlightedEticketCopy = Boolean(
		isHighlighted && highlightEticket && authorizationHasEticket(authorization, highlightEticket),
	);

	const focusAddETicketButton = useCallback((): void => {
		window.setTimeout(() => {
			if (canCross) {
				addETicketButtonRef.current?.focus();
			}
		}, 300);
	}, [canCross]);

	const handleAccordionChange = (_: React.SyntheticEvent, isExpanded: boolean): void => {
		setExpanded(isExpanded);
		if (isExpanded && !shouldFocusHighlightedEticketCopy) {
			focusAddETicketButton();
		}
	};

	useEffect(() => {
		if (isHighlighted) {
			setExpanded(true);
		}
	}, [isHighlighted]);

	useEffect(() => {
		if (isHighlighted && expanded && !shouldFocusHighlightedEticketCopy) {
			focusAddETicketButton();
		}
	}, [isHighlighted, expanded, shouldFocusHighlightedEticketCopy, focusAddETicketButton]);

	const statusChip = ((): { label: string; color: 'default' | 'warning' | 'info' } => {
		if (isFullyRefunded) return { label: 'Reembolsada', color: 'default' };
		if (isPartiallyRefunded) return { label: 'Reembolso parcial', color: 'warning' };
		return { label: getAuthorizationStateName(authorization.state), color: 'info' };
	})();

	const refundTooltip = ((): string => {
		if (isPaymentGateway) {
			if (isFullyRefunded) {
				return 'Autorización reembolsada al 100%. No queda saldo para devolver.';
			}
			if (!canRefund) {
				return 'No hay saldo disponible para devolver en esta autorización.';
			}
			return `Podés devolver hasta ${fmt(
				maxRefundable,
			)} (saldo autorizado). Los cobros por pasarela no se cruzan contra e-tickets; la devolución también hay que gestionarla con ${
				authorization.chargedAgainstOptionName
			}.`;
		}
		if (isFullyRefunded) {
			return 'Autorización reembolsada al 100%. No queda saldo para devolver ni cruzar.';
		}
		if (!canRefund && hasActiveCrosses) {
			return `No hay monto sin cruzar. Hay ${fmt(
				activeCrossed,
			)} en e-tickets: desvinculá los boletos antes de devolver.`;
		}
		if (!canRefund) {
			return 'No hay saldo disponible para devolver en esta autorización.';
		}
		if (hasActiveCrosses) {
			return `Podés devolver hasta ${fmt(maxRefundable)} (monto sin cruzar). Para devolver lo cruzado (${fmt(
				activeCrossed,
			)}), primero desvinculá los e-tickets.`;
		}
		return `Podés devolver hasta ${fmt(
			maxRefundable,
		)} (monto sin cruzar). También debés gestionar la devolución con el procesador de pagos.`;
	})();

	return (
		<Accordion
			expanded={expanded}
			onChange={handleAccordionChange}
			sx={{
				borderRadius: 2,
				overflow: 'hidden',
				border: isHighlighted ? 2 : 1,
				borderColor: isHighlighted ? 'primary.main' : 'divider',
				'&:before': { display: 'none' },
				boxShadow: isHighlighted ? 2 : 0,
				bgcolor: 'background.paper',
			}}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				sx={{
					minHeight: 112,
					px: 2,
					py: 1.25,
					alignItems: 'center',
					'&.Mui-expanded': { minHeight: 112 },
					'& .MuiAccordionSummary-content': { my: 0, mx: 0, width: '100%' },
					bgcolor: isHighlighted ? 'primary.50' : 'background.paper',
					'&:hover': { bgcolor: isHighlighted ? 'primary.100' : 'grey.50' },
				}}
			>
				<AuthorizationCardSummary
					authorizationId={authorization.id}
					authorizationCode={authorization.authorizationCode}
					chargedAgainstOptionName={authorization.chargedAgainstOptionName}
					carrierCode={carrierCode}
					paymentDueDate={authorization.paymentDueDate}
					createdBy={authorization.createdBy}
					ticketCount={ticketCount}
					statusChip={statusChip}
					isFullyRefunded={isFullyRefunded}
					hasRefunds={hasRefunds}
					totalRefundedLabel={fmt(totalRefunded)}
					partialRefundCaption={
						hasRefunds && !isFullyRefunded
							? `Reembolsado ${fmt(totalRefunded)} · Saldo ${fmt(currentBalance)}`
							: undefined
					}
					isPaymentGateway={isPaymentGateway}
					authorizedLabel={fmt(originalAuthorized)}
					crossedLabel={fmt(activeCrossed)}
					pendingCrossLabel={fmt(pendingToCross)}
					activeCrossed={activeCrossed}
					pendingToCross={pendingToCross}
					onCopyAuthId={handleCopyAuthId}
					onCopyAuthCode={handleCopyAuthCode}
				/>
			</AccordionSummary>

			<AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 0.75,
							alignItems: 'center',
							justifyContent: 'flex-end',
						}}
					>
						<Tooltip title={refundTooltip}>
							<span>
								<Button
									variant="outlined"
									color="error"
									size="small"
									startIcon={<CurrencyExchangeIcon />}
									onClick={() => onRefund?.(authorization.id)}
									disabled={!canRefund}
								>
									Devolución{canRefund ? ` · hasta ${fmt(maxRefundable)}` : ''}
								</Button>
							</span>
						</Tooltip>
						{!isPaymentGateway && (
							<Tooltip
								title={
									!canCross
										? 'No hay saldo sin cruzar disponible'
										: 'Cruzar contra un e-ticket con el saldo autorizado.'
								}
							>
								<span>
									<Button
										ref={addETicketButtonRef}
										variant="outlined"
										size="small"
										startIcon={<ReceiptIcon />}
										onClick={() => onAddETicket?.(authorization.id)}
										disabled={!canCross}
									>
										Agregar E-Ticket
									</Button>
								</span>
							</Tooltip>
						)}
						{isAdminOrSuperAdminOrOperator && (
							<Tooltip title="Comando Amadeus FP CASH para el GDS">
								<span>
									<Button
										variant="outlined"
										color="secondary"
										size="small"
										startIcon={<CodeIcon />}
										onClick={() => onGenerateCommand?.(authorization.id)}
										disabled={currentBalance === 0}
									>
										Comando GDS
									</Button>
								</span>
							</Tooltip>
						)}
					</Box>

					<Typography variant="caption" color="text.secondary">
						Autorizada {formatDateToShowUser(authorization.paymentDate)}
						{authorization.createdAt ? ` · Registro ${formatDateTimeToShowUser(authorization.createdAt)}` : ''}
					</Typography>

					{hasRefunds && dealId != null && (
						<AuthorizationRefundSaleOrderNote
							authorizationId={authorization.id}
							totalRefunded={totalRefunded}
							coinCode={authorization.coinCode}
							dealId={dealId}
							saleOrderId={saleOrderId}
							isFullyRefunded={isFullyRefunded}
							onError={onRefundNoteError}
						/>
					)}

					<Divider />

					{isPaymentGateway && crosses.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							Cobro por <strong>{authorization.chargedAgainstOptionName}</strong> (pasarela de pago): no se cruza contra
							e-tickets.
						</Typography>
					) : (
						<Box>
							{isPaymentGateway && (
								<Alert severity="warning" sx={{ mb: 1 }}>
									Esta autorización es por pasarela ({authorization.chargedAgainstOptionName}) y no debería tener
									boletos cruzados, pero tiene {ticketCount}. El saldo a devolver no los descuenta: revisá antes de
									devolver.
								</Alert>
							)}
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									mb: 0.75,
									gap: 1,
								}}
							>
								<Typography variant="subtitle2" fontWeight="bold">
									Boletos{ticketCount > 0 ? ` (${ticketCount})` : ''}
								</Typography>
								{currentBalance > 0 && (
									<Typography variant="caption" color="text.secondary">
										{crossedPercent}% cruzado
									</Typography>
								)}
							</Box>

							{currentBalance > 0 && (
								<LinearProgress
									variant="determinate"
									value={crossedPercent}
									color="success"
									sx={{ height: 4, borderRadius: 1, mb: 1 }}
								/>
							)}

							<AuthorizationTicketsList
								crosses={crosses}
								authorizationId={authorization.id}
								coinCode={authorization.coinCode}
								highlightEticket={shouldFocusHighlightedEticketCopy ? highlightEticket : undefined}
								focusHighlightedEticketCopy={shouldFocusHighlightedEticketCopy}
								operators={operators}
								transactionContext={transactionViewContext}
								pendingToCross={pendingToCross}
								canCross={canCross}
								onCopySuccess={onCopySuccess}
								onUnbindCross={onUnbindCross ? (cross) => onUnbindCross(authorization.id, cross) : undefined}
								onAddETicket={canCross ? () => onAddETicket?.(authorization.id) : undefined}
								onNavigationError={onRefundNoteError}
							/>
						</Box>
					)}

					{/* Sección de Devoluciones */}
					{authorization.refunded && authorization.refunded.length > 0 && (
						<>
							<Divider sx={{ my: 1.5 }} />

							<Box>
								<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
									Devoluciones
								</Typography>

								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Monto</TableCell>
												<TableCell>Fecha</TableCell>
												<TableCell>Creado por</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{authorization.refunded.map((refund, index) => (
												<TableRow key={index}>
													<TableCell>
														<Typography color="error" fontWeight="bold">
															-{refund.amountRefunded} {refund.coinCode}
														</Typography>
													</TableCell>
													<TableCell>{formatDateTimeToShowUser(refund.refundedAt)}</TableCell>
													<TableCell>{refund.refundedBy}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>

								<Box sx={{ mt: 1, p: 1.5, bgcolor: isFullyRefunded ? 'grey.100' : 'error.50', borderRadius: 1 }}>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={4}>
											<Typography variant="caption" color="text.secondary">
												Total reembolsado
											</Typography>
											<Typography variant="body1" fontWeight="bold" color="error.main">
												{fmt(totalRefunded)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												({authorization.refunded.length}{' '}
												{authorization.refunded.length === 1 ? 'devolución' : 'devoluciones'})
											</Typography>
										</Grid>

										<Grid item xs={12} sm={4}>
											<Typography variant="caption" color="text.secondary">
												Monto original
											</Typography>
											<Typography variant="body1" fontWeight="bold">
												{fmt(originalAuthorized)}
											</Typography>
										</Grid>

										<Grid item xs={12} sm={4}>
											<Typography variant="caption" color="text.secondary">
												{isFullyRefunded ? 'Estado' : 'Saldo vigente'}
											</Typography>
											<Typography
												variant="body1"
												fontWeight="bold"
												color={currentBalance === 0 ? 'text.secondary' : 'success.main'}
											>
												{isFullyRefunded ? 'Reembolsada al 100%' : fmt(currentBalance)}
											</Typography>
											{!isFullyRefunded && (
												<Typography variant="caption" color="text.secondary">
													Disponible para cruzar o devolver
												</Typography>
											)}
										</Grid>
									</Grid>
								</Box>
							</Box>
						</>
					)}
				</Box>
			</AccordionDetails>
		</Accordion>
	);
};
